import i18next from 'i18next';
import { toast } from 'sonner';

import { useDelegationStore } from '@/store/delegation.store.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

const t = (k, fb) => i18next.t(k, { defaultValue: fb });

const ERROR_KEYS = {
    'conflict.delegationOverlap': ['delegation:messages.overlap', 'An active overlapping delegation already exists for this user.'],
    'delegation.sameUser': ['delegation:messages.sameUser', 'You cannot delegate to yourself.'],
    'delegation.eventBusUnavailable': ['delegation:messages.eventBusUnavailable', 'Delegation was not saved because the event bus is unavailable. Try again.'],
};

function errMsg(e, fb) {
    const code = e?.response?.data?.error;
    const mapped = ERROR_KEYS[code];
    if (mapped) return t(mapped[0], { defaultValue: mapped[1] });
    return getIntegrationErrorMessage(e) || fb || t('delegation:messages.loadFailed', {
        defaultValue: 'Could not load list',
    });
}

export class DelegationIntegration {
    constructor(service) {
        this.service = service;
    }

    async fetchAll(params = {}) {
        const s = useDelegationStore.getState();
        const { columnFilters = s.columnFilters, ...requestParams } = params;
        const q = {
            ...s.searchParams,
            ...requestParams,
            ...mapColumnFiltersForQuery(columnFilters),
        };
        s.setSearchParams(requestParams);
        s.setColumnFilters(columnFilters);
        s.setLoading(true);
        s.setError(null);
        try {
            const r = await this.service.getAll(q);
            const rows = Array.isArray(r.data) ? r.data : [];
            s.setItems(rows);
            const pag = r.pagination || {};
            s.setPaginationData({
                page: pag.page || q.page || 1,
                pageSize: pag.pageSize || q.pageSize || 20,
                total: pag.total ?? rows.length,
                totalPages: pag.totalPages || 1,
            });
            s.setUniqueValues(r.uniqueValues || {});
            return { success: true, data: rows, uniqueValues: r.uniqueValues || {} };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.loadFailed', { defaultValue: 'Could not load list' }));
            s.setError(m);
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setLoading(false);
        }
    }

    async fetchMine() {
        const s = useDelegationStore.getState();
        s.setLoading(true);
        try {
            const r = await this.service.mine();
            s.setMine(Array.isArray(r.data) ? r.data : []);
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.loadFailed', { defaultValue: 'Could not load list' }));
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setLoading(false);
        }
    }

    async fetchIncoming({ silent = false } = {}) {
        const s = useDelegationStore.getState();
        if (!silent) s.setLoading(true);
        try {
            const r = await this.service.toMe();
            s.setIncoming(Array.isArray(r.data) ? r.data : []);
            s.setActingPrincipalIds(r.actingPrincipalIds);
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.loadFailed', { defaultValue: 'Could not load list' }));
            if (!silent) toast.error(m);
            return { success: false, error: m };
        } finally {
            if (!silent) s.setLoading(false);
        }
    }

    async create(d, { silent = false } = {}) {
        try {
            const r = await this.service.create(d);
            if (!silent) {
                toast.success(t('delegation:messages.createSuccess', { defaultValue: 'Delegation created' }));
                await this.fetchMine();
            }
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.createFailed', { defaultValue: 'Could not create delegation' }));
            if (!silent) toast.error(m);
            return { success: false, error: m };
        }
    }

    async createMany(payloads) {
        let created = 0;
        let skippedOverlap = 0;
        let lastError = '';
        const overlapText = t('delegation:messages.overlap', {
            defaultValue: 'An active overlapping delegation already exists for this user.',
        });
        for (const payload of payloads || []) {
            const r = await this.create(payload, { silent: true });
            if (r.success) {
                created += 1;
                continue;
            }
            if (r.error === overlapText) {
                skippedOverlap += 1;
                continue;
            }
            lastError = r.error || lastError;
        }
        if (created) {
            toast.success(skippedOverlap
                ? t('delegation:messages.createPartial', {
                    defaultValue: 'New delegations were created; users who already had an active window were skipped.',
                })
                : t('delegation:messages.createSuccess', { defaultValue: 'Delegation created' }));
            await this.fetchMine();
            return { success: true };
        }
        if (skippedOverlap && !lastError) {
            toast.error(t('delegation:messages.alreadyCovered', {
                defaultValue: 'The selected users already have an active delegation. Add a new vekil or revoke the existing one.',
            }));
            return { success: false, error: overlapText };
        }
        if (lastError) {
            const message = getIntegrationErrorMessage({ message: lastError }) || lastError;
            toast.error(message);
            return { success: false, error: message };
        }
        return { success: false };
    }

    async adminCreate(d) {
        try {
            const r = await this.service.adminCreate(d);
            toast.success(t('delegation:messages.createSuccess', { defaultValue: 'Delegation created' }));
            await this.fetchAll();
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.createFailed', { defaultValue: 'Could not create delegation' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async revoke(id, { refresh = 'mine' } = {}) {
        try {
            const r = await this.service.revoke(id);
            toast.success(t('delegation:messages.revokeSuccess', { defaultValue: 'Delegation revoked' }));
            if (refresh === 'admin') await this.fetchAll();
            else await this.fetchMine();
            return { success: true };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.revokeFailed', { defaultValue: 'Could not cancel' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async decline(id) {
        try {
            const r = await this.service.decline(id);
            toast.success(t('delegation:messages.declineSuccess', { defaultValue: 'Delegation rejected' }));
            await this.fetchIncoming();
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('delegation:messages.declineFailed', { defaultValue: 'Reddedilemedi' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }
}
